// CampaignsList.jsx — proc-v3 (F2.3d): TERZA VISTA DEL MONOLITE in React,
// lista "Campagne outreach", flag-gated (shell_proc_v3_campaigns, default OFF).
//
// Replica 1:1 renderCampaigns di proc-v0.js:7100-7148: stesso titolo/sottotitolo,
// stessi bottoni (Quick RFQ ambra / + Nuova campagna), stesse pill filtro
// (Tutte/Regolare/RFQ rapida/Attiva/Vinta) + select tag (solo se esistono tag),
// stesse colonne (Nome, Tipo, Priorità, Target, Tag, Mittente, Stato badge,
// Fornitori), stesso ordinamento di default (Nome asc — parità byte-per-byte
// via _nameRank, vedi api.readCampaigns), stesso click riga → dettaglio V1.
// La tabella è il componente condiviso client/src/ui/DataTable.jsx (nessuna
// prop nuova: la vista non ha selezione/bulk nel v1).
//
// CONFINI (pattern f23c): dettaglio campagna e modale nuova campagna = V1
// (deleghe in api.js); MAI 'nav:badge' da questo modulo.
//
// STATO (parità di persistenza col v1): filtro pill + filtro tag vivono nella
// FACTORY (pagina), come i binding lessicali campFilter/campTagFilter del v1
// (proc-v0.js:7098-7099): sopravvivono a nav via/ritorno e ai refresh dati.
// NB: sono binding LESSICALI nel v1 (non su window) ⇒ v1 e v3 non condividono
// il valore corrente, ma partono dagli stessi default ('all', '').
//
// Il chrome usa le stesse classi Tailwind del v1 (il componente vive nel
// documento del monolite, #campaigns-content) — parità visiva senza fork.

import React from 'react';
import { DataTable } from '../ui/DataTable.jsx';
import {
  tCmp, CMP_TEXTS, CMP_FILTER_PILLS, defaultCampaignFilters, readCampaigns,
  stateBadgeClass, stateLabel, cmpTargetFilterText, cmpTagsFilterText,
  cmpIdentityFilterText, cmpStateFilterText,
  openQuickRfq, openNewCampaign, openCampaignDetail,
} from './api.js';

export function CampaignsList({ win, tick, filters }) {
  // filtri di vista: stato React specchiato sull'oggetto della factory (write-through)
  const [f, setFState] = React.useState(() => ({ ...filters }));
  const setF = (patch) => {
    Object.assign(filters, patch);
    setFState({ ...filters });
  };

  // dati: letti dallo store del monolite a ogni refresh (tick dal wrapper) o
  // cambio filtri — stessa composizione del v1 (api.readCampaigns)
  const data = React.useMemo(
    () => readCampaigns(win, f),
    [win, tick, f.filter, f.tagFilter],
  );

  const t = (k) => tCmp(win, k);

  const columns = [
    {
      key: 'name',
      label: t('col.name'),
      sortable: true,
      sortValue: (r) => r._v1Rank.name, // rank v1 ⇒ ordine byte-per-byte anche al click ('10'<'2' come v1)
      filterText: (r) => String(r.name == null ? '' : r.name),
      render: (r, ctx) => <span className="font-medium"><ctx.Mark text={r.name} tokens={ctx.tokens} /></span>,
    },
    {
      key: 'kind',
      label: t('col.kind'),
      sortable: true,
      sortValue: (r) => r._v1Rank.kind, // rank v1
      render: (r, ctx) => (
        <span className={(r.kind === 'quick' ? 'bg-amber-100 text-amber-800' : 'bg-cyan-100 text-cyan-800') + ' text-xs px-2 py-0.5 rounded'}>
          <ctx.Mark text={String(r.kind == null ? '' : r.kind)} tokens={ctx.tokens} />
        </span>
      ),
    },
    {
      key: 'priority',
      label: t('col.priority'),
      sortable: true,
      sortValue: (r) => r._v1Rank.priority, // rank v1
      render: (r, ctx) => {
        const cls = r.priority === 'high' ? 'bg-rose-100 text-rose-800' : r.priority === 'low' ? 'bg-slate-100 text-slate-500' : 'bg-slate-100 text-slate-700';
        return (
          <span className={cls + ' text-xs px-2 py-0.5 rounded'}>
            <ctx.Mark text={String(r.priority == null ? '' : r.priority)} tokens={ctx.tokens} />
          </span>
        );
      },
    },
    {
      key: 'target',
      label: t('col.target'),
      sortable: true,
      sortValue: (r) => r._v1Rank.target, // rank v1
      filterText: cmpTargetFilterText,
      render: (r, ctx) => (
        <code className="bg-slate-100 px-1 rounded text-xs"><ctx.Mark text={r.target || '—'} tokens={ctx.tokens} /></code>
      ),
    },
    {
      key: 'tags',
      label: CMP_TEXTS.tagsCol,
      sortable: true,
      sortValue: (r) => r._v1Rank.tags, // rank v1 (String(array), come il v1)
      filterText: cmpTagsFilterText,
      render: (r, ctx) => ((r.tags && r.tags.length) ? (
        <>
          {r.tags.map((tg, i) => (
            <span key={i} className="bg-slate-100 text-slate-700 text-[10px] px-1.5 py-0.5 rounded inline-block mr-1 mb-0.5">
              <ctx.Mark text={tg} tokens={ctx.tokens} />
            </span>
          ))}
        </>
      ) : <span className="text-slate-300"><ctx.Mark text="—" tokens={ctx.tokens} /></span>),
    },
    {
      key: '_identityName',
      label: t('col.sending_as'),
      sortable: true,
      sortValue: (r) => r._v1Rank._identityName, // rank v1
      filterText: cmpIdentityFilterText,
      render: (r, ctx) => (
        <>
          <ctx.Mark text={r._identityName} tokens={ctx.tokens} />
          {r._identityEmail ? (
            <div className="text-[10px] text-slate-500"><ctx.Mark text={r._identityEmail} tokens={ctx.tokens} /></div>
          ) : null}
        </>
      ),
    },
    {
      key: 'state',
      label: t('col.state'),
      sortable: true,
      sortValue: (r) => r._v1Rank.state, // rank v1 (lo stato RAW, non la label — come il v1)
      filterText: (r) => cmpStateFilterText(win, r),
      render: (r, ctx) => (
        <span className={stateBadgeClass(r.state) + ' text-xs px-2 py-0.5 rounded'}>
          <ctx.Mark text={stateLabel(win, r.state)} tokens={ctx.tokens} />
        </span>
      ),
    },
    {
      key: '_supCount',
      label: t('col.suppliers'),
      sortable: true,
      sortValue: (r) => r._v1Rank._supCount, // rank v1 (type number)
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div>
          <h1 className="text-2xl font-bold">{t('cmp.title')}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{t('cmp.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button
            id="btn-quick-rfq"
            type="button"
            className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded text-sm font-medium"
            onClick={() => openQuickRfq(win)}
          >{t('cmp.quick_rfq')}</button>
          <button
            id="btn-new-campaign"
            type="button"
            className="bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded text-sm"
            onClick={() => openNewCampaign(win)}
          >{t('cmp.new')}</button>
        </div>
      </div>
      <div className="flex gap-2 mb-3 items-center text-xs">
        {CMP_FILTER_PILLS.map(([id, lbl]) => (
          <button
            key={id}
            type="button"
            data-f={id}
            className={'filter-pill ' + (f.filter === id ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200') + ' px-2.5 py-1 rounded'}
            onClick={() => setF({ filter: id })}
          >{lbl}</button>
        ))}
        {data.allCampTags.length ? (
          <>
            <span className="text-slate-300 mx-1">|</span>
            <select
              id="camp-tag-filter"
              className="border border-slate-300 rounded px-2 py-1 text-xs"
              value={f.tagFilter}
              onChange={(e) => setF({ tagFilter: e.target.value })}
            >
              <option value="">{CMP_TEXTS.anyTag}</option>
              {data.allCampTags.map((tg) => <option key={tg} value={tg}>{tg}</option>)}
            </select>
          </>
        ) : null}
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3" id="campaigns-table-mount">
        <DataTable
          columns={columns}
          rows={data.rows}
          rowKey="id"
          sort={{ key: 'name', dir: 'asc' }}
          filterable
          onRowClick={(row) => openCampaignDetail(win, row.id)}
          emptyMessage={CMP_TEXTS.emptyMessage}
        />
      </div>
    </div>
  );
}

// Factory (pattern createProcV3Suppliers): il register/install la aggancia al
// WRAP di window.renderCampaigns. render() idempotente sul container; unmount()
// libera il container (il monolite resta vivo per le altre viste).
export function createProcV3Campaigns(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;
  // filtri di vista a livello di PAGINA, come campFilter/campTagFilter v1
  const filters = defaultCampaignFilters();

  function render() {
    const container = win.document.getElementById('campaigns-content');
    if (!container) throw new Error('#campaigns-content assente');
    if (!root || mountedEl !== container || !container.isConnected) {
      if (root) { try { root.unmount(); } catch (e) { /* già smontato */ } }
      container.replaceChildren(); // eventuali resti del v1 (catch-up)
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1; // ogni chiamata del wrapper = refresh dati (rerenderAll, modali v1, rientro)
    root.render(<CampaignsList win={win} tick={tick} filters={filters} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
    // filters NON si azzerano: parità col v1 (binding di pagina)
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
